// Pure decision/formatting helpers for the lost-chargeback resolve panel (PROD-7808 T11).
// No I/O here — details.jsx passes data in and renders the results (unit-tested in
// resolve_helpers.test.js). Shapes follow CONTRACT-wave2.md.

// Panel shows only for SUPER_USER on a lost dispute that isn't already closed out.
export const can_show_resolve_panel = (dispute, access_type) => {
  if (!dispute || access_type !== 'SUPER_USER') return false;
  if (String(dispute.status || '').toLowerCase() !== 'lost') return false;
  const s = String(dispute.internal_status || '').toUpperCase();
  return s !== 'RESOLVED' && s !== 'NO_ACTION';
};

export const session_counts = (sessions) => {
  const s = sessions || {};
  return {
    one_on_one: Array.isArray(s.one_on_one) ? s.one_on_one.length : 0,
    gc_registrations: Array.isArray(s.gc_registrations) ? s.gc_registrations.length : 0,
    repeat_one_on_one: Array.isArray(s.repeat_one_on_one) ? s.repeat_one_on_one.length : 0
  };
};

// repeat_slot_text: one weekly 1:1 repeat template line — repeat_time's weekday+time IS the slot;
// repeat_days is the per-week count ("Nx"). fmt_slot formats the datetime as weekday+time.
export const repeat_slot_text = (r, fmt_slot = (v) => v) => {
  const rr = r || {};
  const parts = [rr.repeat_time ? fmt_slot(rr.repeat_time) : 'unknown slot'];
  if (rr.duration) parts.push(`${rr.duration} min`);
  if (rr.repeat_days > 1) parts.push(`${rr.repeat_days}x/week`);
  return parts.join(' · ');
};

// credits_text: renewal branch reverses nothing (entitlement lives on the transaction).
export const credits_text = (credits) => {
  const c = credits || {};
  if (c.type_branch === 'renewal') {
    return 'no monetary credits to reverse (renewal — entitlement on transaction)';
  }
  const parts = [
    `granted ${c.granted ?? 0}`,
    `remaining ${c.remaining ?? 0}`,
    `reversible ${c.reversible ?? 0}`
  ];
  if (c.shortfall) parts.push(`shortfall ${c.shortfall}`);
  return `reverse ${c.reversible ?? 0} credit(s) (${parts.join(', ')})`;
};

// payments_warning_text: the panel's loudest warning — customer kept paying after disputing.
export const payments_warning_text = (payments, fmt_dt = (v) => v) => {
  const p = payments || {};
  if (!p.count) return null;
  const latest = p.latest_date ? ` (latest ${fmt_dt(p.latest_date)})` : '';
  return `Customer has PAID ${p.count} time${p.count === 1 ? '' : 's'} since this dispute${latest} — consider retaining instead`;
};

// entitlement_warning_text: B4 — never hide remaining entitlement on an already-cancelled membership.
export const entitlement_warning_text = (membership, fmt_dt = (v) => v) => {
  const m = membership || {};
  if (!m.already_cancelled || !m.entitled_until) return null;
  return `Membership already cancelled — entitled until ${fmt_dt(m.entitled_until)}`;
};

// summarize_preview_for_confirm: the terse restatement shown on the inline confirm step.
export const summarize_preview_for_confirm = (preview, fmt_dt = (v) => v) => {
  const p = preview || {};
  const m = p.membership || {};
  const counts = session_counts(p.sessions);
  const membership = m.already_cancelled
    ? `Membership: already cancelled${m.entitled_until ? ` — entitled until ${fmt_dt(m.entitled_until)}` : ''}`
    : `Membership: cancel immediately (txn #${m.transaction_id ?? '?'}, ${m.subscription_status || 'unknown status'})`;
  return [
    membership,
    `Sessions: cancel ${counts.one_on_one} upcoming 1:1 session(s) + ${counts.gc_registrations} GC registration(s) + ${counts.repeat_one_on_one} weekly repeat slot(s)`,
    `Credits: ${credits_text(p.credits)}`
  ];
};

// steps_progress: non-empty only when a previous run left state behind (resume view).
export const steps_progress = (steps) => {
  const s = steps || {};
  const rows = ['membership', 'sessions', 'credits']
    .filter((k) => s[k])
    .map((k) => ({ step: k, state: s[k] }));
  return rows.some((r) => r.state !== 'PENDING') ? rows : [];
};

export const resolve_button_label = (steps) =>
  steps_progress(steps).length > 0 ? 'Resume resolve actions' : 'Apply resolve actions';

// classify_execute_response: bucket the HTTP-200 execute payload for rendering.
// 'error' = missing/empty envelope (nothing to render — don't mislabel as partial);
// 'aborted' = a concurrent run holds a step claim; 'resolved' = fully done; else 'partial'.
export const classify_execute_response = (data) => {
  const d = data || {};
  const failures = Array.isArray(d.failures) ? d.failures : [];
  const has_signal =
    d.aborted_reason || d.finalized || d.internal_status || d.steps || d.results || failures.length;
  if (!has_signal) return { kind: 'error', reason: '', failures: [] };
  if (d.aborted_reason) return { kind: 'aborted', reason: d.aborted_reason, failures };
  if (d.finalized || String(d.internal_status || '').toUpperCase() === 'RESOLVED') {
    return { kind: 'resolved', reason: '', failures };
  }
  return { kind: 'partial', reason: '', failures };
};

// flatten_session_items: results.sessions.items keys are r:<uuid> | o:<uuid> | g:<id> | s:<uuid>.
export const flatten_session_items = (items) =>
  Object.entries(items || {}).map(([key, v]) => {
    let kind = 'GC (one-off)';
    if (key.startsWith('r:')) kind = 'Repeat 1:1';
    else if (key.startsWith('o:')) kind = '1:1';
    else if (key.startsWith('g:')) kind = 'GC (repeat)';
    return { key, kind, outcome: (v && v.outcome) || '', detail: (v && v.detail) || '' };
  });
